import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PrinterIcon } from '@heroicons/react/24/outline';
import tprmService from '../../../services/compliance/tprm.service';
import type {
  RemediationAction,
  RiskAcceptance,
  Vendor,
} from '../../../types/compliance';
import { useUserNames } from '../../../hooks/useUserNames';


const DOMAIN_LABELS: Record<string, string> = {
  data_sensitivity: 'Data Sensitivity',
  system_access: 'System / Access Risk',
  operational: 'Operational Criticality',
  security: 'Security Maturity',
  financial: 'Financial / Contractual',
  compliance: 'Compliance / Regulatory',
  business_context: 'Business Context',
};


const SECTION_LETTER_TO_DOMAIN: Record<string, string> = {
  b: 'data_sensitivity',
  c: 'system_access',
  d: 'operational',
  e: 'security',
  f: 'financial',
  g: 'compliance',
};


const HUMANIZE_ACRONYMS = new Set([
  'mfa', 'sso', 'pci', 'hipaa', 'gdpr', 'rbac', 'rto', 'idp', 'dpa', 'api',
  'tms', 'wms', 'edi', 'iot', 'sla', 'dr', 'sox', 'ccpa', 'hitrust', 'ir',
  'soc', 'iso', 'bcdr',
]);


function humanizeKey(k: string): string {
  return k
    .split('_')
    .map((word) => {
      if (HUMANIZE_ACRONYMS.has(word.toLowerCase())) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}


function formatAnswer(val: unknown): string {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
  return String(val);
}


/** Section-score row — matches Django vendor_report.html weighted
 *  contribution column. Weights come from the vendor's scores object. */
function sectionContribution(score: number | null | undefined, weight: number): string {
  if (score === null || score === undefined) return '—';
  return (Number(score) * weight).toFixed(2);
}


// Section weights must match backend `tprm_scoring.SECTION_WEIGHTS`.
const SECTION_WEIGHTS: Record<string, number> = {
  data_sensitivity: 0.20,
  system_access: 0.20,
  operational: 0.15,
  security: 0.20,
  financial: 0.10,
  compliance: 0.10,
  business_context: 0.05,
};


const DOMAIN_TO_SCORE_FIELD: Record<string, keyof Vendor['scores']> = {
  data_sensitivity: 'score_data_sensitivity',
  system_access: 'score_system_access',
  operational: 'score_operational',
  security: 'score_security',
  financial: 'score_financial',
  compliance: 'score_compliance',
  business_context: 'score_business_context',
};


export default function VendorReport() {
  const { id } = useParams<{ id: string }>();
  const { nameFor } = useUserNames();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [remediations, setRemediations] = useState<RemediationAction[]>([]);
  const [riskAcceptances, setRiskAcceptances] = useState<RiskAcceptance[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setErr(null);
    try {
      const [v, r, a] = await Promise.all([
        tprmService.getVendor(id),
        tprmService.listRemediations(id),
        tprmService.listRiskAcceptances(id),
      ]);
      setVendor(v);
      setRemediations(r);
      setRiskAcceptances(a);
    } catch (error: any) {
      setErr(
        error?.response?.data?.detail || error?.message || 'Unknown error',
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const openRemediations = useMemo(
    () => remediations.filter(
      (r) => r.status === 'open' || r.status === 'in_progress',
    ),
    [remediations],
  );

  const activeRiskAcceptance = useMemo(
    () => riskAcceptances.find((a) => a.is_active) ?? null,
    [riskAcceptances],
  );

  const answerMaps: { letter: string; answers: Record<string, unknown> | null }[] = useMemo(() => {
    if (!vendor) return [];
    return [
      { letter: 'b', answers: vendor.answers_b },
      { letter: 'c', answers: vendor.answers_c },
      { letter: 'd', answers: vendor.answers_d },
      { letter: 'e', answers: vendor.answers_e },
      { letter: 'f', answers: vendor.answers_f },
      { letter: 'g', answers: vendor.answers_g },
    ];
  }, [vendor]);

  if (loading) {
    return <div className="p-8 text-sm text-gray-500">Loading report…</div>;
  }
  if (err) {
    return <div className="p-8 text-sm text-red-600">{err}</div>;
  }
  if (!vendor) return null;

  return (
    <div className="max-w-5xl mx-auto p-8 print:p-0 bg-white dark:bg-gray-900 text-body">
      {/* Header — hidden when printing */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <div>
          <Link
            to={`/compliance/tprm/vendors/${vendor.id}`}
            className="text-sm text-blue-600 hover:underline"
          >
            ← Back to vendor
          </Link>
          <h1 className="text-2xl font-bold mt-2">Vendor Assessment Report</h1>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-highlight"
        >
          <PrinterIcon className="w-4 h-4" />
          Print
        </button>
      </div>

      {/* Title shown only in print */}
      <div className="hidden print:block mb-4">
        <h1 className="text-3xl font-bold">Vendor Assessment Report</h1>
        <div className="text-sm text-gray-600">
          Generated {new Date().toLocaleString()}
        </div>
      </div>

      {/* Vendor summary */}
      <section className="mb-8 border-b border-gray-200 pb-6">
        <h2 className="text-xl font-semibold mb-3">{vendor.name}</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Legal entity: </span>{vendor.legal_entity_name || '—'}</div>
          <div><span className="text-gray-500">Product / Service: </span>{vendor.product_service_name || '—'}</div>
          <div><span className="text-gray-500">Service type: </span>{humanizeKey(vendor.service_type || '') || '—'}</div>
          <div><span className="text-gray-500">Department: </span>{vendor.department || '—'}</div>
          <div><span className="text-gray-500">Business owner: </span>{nameFor(vendor.business_owner_oid) || '—'}</div>
          <div><span className="text-gray-500">Technical owner: </span>{nameFor(vendor.technical_owner_oid) || '—'}</div>
          <div><span className="text-gray-500">Status: </span>{humanizeKey(vendor.status)}</div>
          <div><span className="text-gray-500">Risk tier: </span>{humanizeKey(vendor.scores?.risk_tier || '')}</div>
          <div><span className="text-gray-500">Total score: </span>
            {vendor.scores?.total_score !== null && vendor.scores?.total_score !== undefined
              ? vendor.scores.total_score.toFixed(2)
              : '—'}</div>
          <div><span className="text-gray-500">Next review: </span>{vendor.next_review_date || '—'}</div>
        </div>
        {vendor.purpose && (
          <div className="mt-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Purpose / business justification</div>
            <p className="text-sm whitespace-pre-wrap">{vendor.purpose}</p>
          </div>
        )}
      </section>

      {/* Section scores */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-3">Section Scores</h2>
        <table className="w-full text-sm border border-gray-200 dark:border-gray-700">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800">
              <th className="py-2 px-3 text-left">Section</th>
              <th className="py-2 px-3 text-right">Score (0–100)</th>
              <th className="py-2 px-3 text-right">Weight</th>
              <th className="py-2 px-3 text-right">Weighted</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(DOMAIN_LABELS).map(([domain, label]) => {
              const scoreField = DOMAIN_TO_SCORE_FIELD[domain];
              const score = (vendor.scores as unknown as Record<string, number | null | undefined>)[scoreField as string];
              const weight = SECTION_WEIGHTS[domain] ?? 0;
              return (
                <tr key={domain} className="border-t border-gray-200 dark:border-gray-700">
                  <td className="py-2 px-3">{label}</td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {score !== null && score !== undefined ? Number(score).toFixed(2) : '—'}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {(weight * 100).toFixed(0)}%
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {sectionContribution(score, weight)}
                  </td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-gray-400 dark:border-gray-500 font-semibold">
              <td className="py-2 px-3" colSpan={3}>Total</td>
              <td className="py-2 px-3 text-right tabular-nums">
                {vendor.scores?.total_score !== null && vendor.scores?.total_score !== undefined
                  ? vendor.scores.total_score.toFixed(2)
                  : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Section Q&A */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-3">Assessment Responses</h2>
        {answerMaps.map(({ letter, answers }) => {
          const domain = SECTION_LETTER_TO_DOMAIN[letter];
          const keys = Object.keys(answers || {});
          if (keys.length === 0) return null;
          return (
            <div key={letter} className="mb-5 print:break-inside-avoid">
              <h3 className="text-base font-semibold mb-2">
                {letter.toUpperCase()} — {DOMAIN_LABELS[domain]}
              </h3>
              <table className="w-full text-sm border border-gray-200 dark:border-gray-700">
                <tbody>
                  {keys.map((key) => (
                    <tr
                      key={key}
                      className="border-t border-gray-200 dark:border-gray-700"
                    >
                      <td className="py-1.5 px-3 text-gray-600 dark:text-gray-300 w-1/2">
                        {humanizeKey(key)}
                      </td>
                      <td className="py-1.5 px-3">
                        {formatAnswer((answers as Record<string, unknown>)[key])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </section>

      {/* Open remediations */}
      <section className="mb-8 print:break-inside-avoid">
        <h2 className="text-lg font-semibold mb-3">
          Open Remediations ({openRemediations.length})
        </h2>
        {openRemediations.length === 0 ? (
          <p className="text-sm text-gray-500">No open remediations.</p>
        ) : (
          <table className="w-full text-sm border border-gray-200 dark:border-gray-700">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <th className="py-2 px-3 text-left">Title</th>
                <th className="py-2 px-3 text-left">Status</th>
                <th className="py-2 px-3 text-left">Due</th>
                <th className="py-2 px-3 text-left">Assigned</th>
              </tr>
            </thead>
            <tbody>
              {openRemediations.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-gray-200 dark:border-gray-700"
                >
                  <td className="py-2 px-3">
                    <div className="font-medium">{r.title}</div>
                    {r.description && (
                      <div className="text-xs text-gray-500 whitespace-pre-wrap">
                        {r.description}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3">{humanizeKey(r.status)}</td>
                  <td className="py-2 px-3">{r.due_date || '—'}</td>
                  <td className="py-2 px-3">{nameFor(r.assigned_to_oid) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Latest active risk acceptance */}
      <section className="mb-8 print:break-inside-avoid">
        <h2 className="text-lg font-semibold mb-3">Active Risk Acceptance</h2>
        {!activeRiskAcceptance ? (
          <p className="text-sm text-gray-500">No active risk acceptance recorded.</p>
        ) : (
          <div className="border border-gray-200 dark:border-gray-700 rounded p-3 text-sm">
            <div><span className="text-gray-500">Accepted by: </span>{nameFor(activeRiskAcceptance.accepted_by_oid) || '—'}</div>
            <div><span className="text-gray-500">Accepted at: </span>{new Date(activeRiskAcceptance.accepted_at).toLocaleString()}</div>
            {activeRiskAcceptance.expiry_date && (
              <div><span className="text-gray-500">Expires: </span>{activeRiskAcceptance.expiry_date}</div>
            )}
            <div className="mt-2">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Reason</div>
              <p className="whitespace-pre-wrap">{activeRiskAcceptance.reason}</p>
            </div>
            {activeRiskAcceptance.conditions && (
              <div className="mt-2">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Conditions</div>
                <p className="whitespace-pre-wrap">{activeRiskAcceptance.conditions}</p>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Footer — print-only */}
      <div className="hidden print:block text-xs text-gray-500 mt-8 pt-3 border-t border-gray-200">
        Generated by the GWI Compliance Portal · {new Date().toISOString()}
      </div>
    </div>
  );
}
